import React, { useState, useMemo, useEffect } from "react"
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/card"
import { Button } from "../components/button"
import { Progress } from "../components/progress"
import { Check, AlertTriangle, Minus, X, Gauge, Lightbulb, Users, ArrowRight, Brain, ChevronLeft, ChevronRight, Trophy, Star, CheckCircle2, BarChart3, FileText, RotateCcw, Eye, Ear, BookOpen, Users2 } from 'lucide-react'
import { cn } from "../lib/utils"
import Navbar from "../components/Navbar"
import Sidebar from "../components/Sidebar"
import { Badge } from '../components/badge';
import { toast } from 'react-toastify';
import api from "../sever/Api";
import { generateAIReport } from "../sever/Api";
import { notifyTestCompletion } from "../lib/testUtils";

// Learning Style Assessment Data
const learningStyles = [
  {
    name: "Visual",
    description: "Learn best through seeing, images, diagrams, and visual aids",
    icon: Eye,
    color: "from-blue-500 to-cyan-600",
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
    borderColor: "border-blue-200",
    questions: [
      {
        id: "visual-q1",
        prompt: "When learning something new, I prefer to:",
        statements: [
          { id: "visual-q1-s1", text: "Watch a demonstration", style: "Visual" },
          { id: "visual-q1-s2", text: "Read instructions aloud", style: "Auditory" },
          { id: "visual-q1-s3", text: "Take detailed notes in words", style: "Reading/Writing" },
          { id: "visual-q1-s4", text: "Practice by doing it myself", style: "Kinesthetic" },
        ],
      },
      {
        id: "visual-q2",
        prompt: "If I need directions, I prefer:",
        statements: [
          { id: "visual-q2-s1", text: "Looking at a map", style: "Visual" },
          { id: "visual-q2-s2", text: "Listening to someone explain", style: "Auditory" },
          { id: "visual-q2-s3", text: "Reading written directions", style: "Reading/Writing" },
          { id: "visual-q2-s4", text: "Walking the route once", style: "Kinesthetic" },
        ],
      },
      {
        id: "visual-q3",
        prompt: "I remember information best when:",
        statements: [
          { id: "visual-q3-s1", text: "I see diagrams and charts", style: "Visual" },
          { id: "visual-q3-s2", text: "I hear explanations", style: "Auditory" },
          { id: "visual-q3-s3", text: "I read descriptions", style: "Reading/Writing" },
          { id: "visual-q3-s4", text: "I use it in real life", style: "Kinesthetic" },
        ],
      },
      {
        id: "visual-q4",
        prompt: "During a lecture, I:",
        statements: [
          { id: "visual-q4-s1", text: "Focus on visual aids/slides", style: "Visual" },
          { id: "visual-q4-s2", text: "Listen to the speaker's tone", style: "Auditory" },
          { id: "visual-q4-s3", text: "Read along in the textbook", style: "Reading/Writing" },
          { id: "visual-q4-s4", text: "Try examples on my own", style: "Kinesthetic" },
        ],
      },
      {
        id: "visual-q5",
        prompt: "I find it easier to understand:",
        statements: [
          { id: "visual-q5-s1", text: "A picture of a process", style: "Visual" },
          { id: "visual-q5-s2", text: "Someone explaining it to me", style: "Auditory" },
          { id: "visual-q5-s3", text: "A written explanation", style: "Reading/Writing" },
          { id: "visual-q5-s4", text: "Doing it step-by-step", style: "Kinesthetic" },
        ],
      },
    ],
  },
  {
    name: "Auditory",
    description: "Learn best through listening, speaking, and verbal communication",
    icon: Ear,
    color: "from-green-500 to-emerald-600",
    bgColor: "bg-green-50",
    textColor: "text-green-700",
    borderColor: "border-green-200",
    questions: [
      {
        id: "auditory-q1",
        prompt: "I prefer learning by:",
        statements: [
          { id: "auditory-q1-s1", text: "Watching videos silently", style: "Visual" },
          { id: "auditory-q1-s2", text: "Listening to lectures or podcasts", style: "Auditory" },
          { id: "auditory-q1-s3", text: "Reading study guides", style: "Reading/Writing" },
          { id: "auditory-q1-s4", text: "Doing practical activities", style: "Kinesthetic" },
        ],
      },
      {
        id: "auditory-q2",
        prompt: "I remember names better than faces:",
        statements: [
          { id: "auditory-q2-s1", text: "Strongly disagree", style: "Visual" },
          { id: "auditory-q2-s2", text: "Agree", style: "Auditory" },
          { id: "auditory-q2-s3", text: "Neutral", style: "Reading/Writing" },
          { id: "auditory-q2-s4", text: "Strongly agree", style: "Kinesthetic" },
        ],
      },
      {
        id: "auditory-q3",
        prompt: "When studying, I:",
        statements: [
          { id: "auditory-q3-s1", text: "Draw mind maps", style: "Visual" },
          { id: "auditory-q3-s2", text: "Read aloud or listen to recordings", style: "Auditory" },
          { id: "auditory-q3-s3", text: "Write summaries", style: "Reading/Writing" },
          { id: "auditory-q3-s4", text: "Role-play or act out concepts", style: "Kinesthetic" },
        ],
      },
      {
        id: "auditory-q4",
        prompt: "In a group discussion, I:",
        statements: [
          { id: "auditory-q4-s1", text: "Sketch ideas on paper", style: "Visual" },
          { id: "auditory-q4-s2", text: "Focus on what people say", style: "Auditory" },
          { id: "auditory-q4-s3", text: "Take detailed notes", style: "Reading/Writing" },
          { id: "auditory-q4-s4", text: "Act out possible scenarios", style: "Kinesthetic" },
        ],
      },
      {
        id: "auditory-q5",
        prompt: "I find it easier to remember:",
        statements: [
          { id: "auditory-q5-s1", text: "Shapes and colors", style: "Visual" },
          { id: "auditory-q5-s2", text: "Voices and sound patterns", style: "Auditory" },
          { id: "auditory-q5-s3", text: "Written text", style: "Reading/Writing" },
          { id: "auditory-q5-s4", text: "How something feels", style: "Kinesthetic" },
        ],
      },
    ],
  },
  {
    name: "Reading/Writing",
    description: "Learn best through reading, writing, and text-based materials",
    icon: BookOpen,
    color: "from-purple-500 to-violet-600",
    bgColor: "bg-purple-50",
    textColor: "text-purple-700",
    borderColor: "border-purple-200",
    questions: [
      {
        id: "reading-q1",
        prompt: "I learn best when I:",
        statements: [
          { id: "reading-q1-s1", text: "See diagrams", style: "Visual" },
          { id: "reading-q1-s2", text: "Hear someone explain", style: "Auditory" },
          { id: "reading-q1-s3", text: "Read or write about it", style: "Reading/Writing" },
          { id: "reading-q1-s4", text: "Try it myself", style: "Kinesthetic" },
        ],
      },
      {
        id: "reading-q2",
        prompt: "I prefer instructions to be:",
        statements: [
          { id: "reading-q2-s1", text: "Shown with images", style: "Visual" },
          { id: "reading-q2-s2", text: "Given verbally", style: "Auditory" },
          { id: "reading-q2-s3", text: "Written in detail", style: "Reading/Writing" },
          { id: "reading-q2-s4", text: "Demonstrated physically", style: "Kinesthetic" },
        ],
      },
      {
        id: "reading-q3",
        prompt: "I often:",
        statements: [
          { id: "reading-q3-s1", text: "Draw concepts", style: "Visual" },
          { id: "reading-q3-s2", text: "Repeat information aloud", style: "Auditory" },
          { id: "reading-q3-s3", text: "Make lists or write summaries", style: "Reading/Writing" },
          { id: "reading-q3-s4", text: "Build models or prototypes", style: "Kinesthetic" },
        ],
      },
      {
        id: "reading-q4",
        prompt: "For a presentation, I usually:",
        statements: [
          { id: "reading-q4-s1", text: "Use images and visuals", style: "Visual" },
          { id: "reading-q4-s2", text: "Record voice notes", style: "Auditory" },
          { id: "reading-q4-s3", text: "Prepare detailed scripts", style: "Reading/Writing" },
          { id: "reading-q4-s4", text: "Rehearse movements", style: "Kinesthetic" },
        ],
      },
      {
        id: "reading-q5",
        prompt: "When learning, I like:",
        statements: [
          { id: "reading-q5-s1", text: "Infographics", style: "Visual" },
          { id: "reading-q5-s2", text: "Audio explanations", style: "Auditory" },
          { id: "reading-q5-s3", text: "Textbooks and articles", style: "Reading/Writing" },
          { id: "reading-q5-s4", text: "Hands-on activities", style: "Kinesthetic" },
        ],
      },
    ],
  },
  {
    name: "Kinesthetic",
    description: "Learn best through hands-on experience, movement, and physical activity",
    icon: Users2,
    color: "from-orange-500 to-red-600",
    bgColor: "bg-orange-50",
    textColor: "text-orange-700",
    borderColor: "border-orange-200",
    questions: [
      {
        id: "kinesthetic-q1",
        prompt: "I understand best when:",
        statements: [
          { id: "kinesthetic-q1-s1", text: "I see visual examples", style: "Visual" },
          { id: "kinesthetic-q1-s2", text: "I listen to explanations", style: "Auditory" },
          { id: "kinesthetic-q1-s3", text: "I read detailed instructions", style: "Reading/Writing" },
          { id: "kinesthetic-q1-s4", text: "I physically do the task", style: "Kinesthetic" },
        ],
      },
      {
        id: "kinesthetic-q2",
        prompt: "I enjoy:",
        statements: [
          { id: "kinesthetic-q2-s1", text: "Looking at art", style: "Visual" },
          { id: "kinesthetic-q2-s2", text: "Listening to music", style: "Auditory" },
          { id: "kinesthetic-q2-s3", text: "Reading stories", style: "Reading/Writing" },
          { id: "kinesthetic-q2-s4", text: "Building or fixing things", style: "Kinesthetic" },
        ],
      },
      {
        id: "kinesthetic-q3",
        prompt: "When solving a problem, I:",
        statements: [
          { id: "kinesthetic-q3-s1", text: "Sketch possible solutions", style: "Visual" },
          { id: "kinesthetic-q3-s2", text: "Talk it out", style: "Auditory" },
          { id: "kinesthetic-q3-s3", text: "Write down pros and cons", style: "Reading/Writing" },
          { id: "kinesthetic-q3-s4", text: "Test different approaches physically", style: "Kinesthetic" },
        ],
      },
      {
        id: "kinesthetic-q4",
        prompt: "In class, I prefer:",
        statements: [
          { id: "kinesthetic-q4-s1", text: "Watching videos", style: "Visual" },
          { id: "kinesthetic-q4-s2", text: "Listening to teachers", style: "Auditory" },
          { id: "kinesthetic-q4-s3", text: "Reading handouts", style: "Reading/Writing" },
          { id: "kinesthetic-q4-s4", text: "Doing experiments", style: "Kinesthetic" },
        ],
      },
      {
        id: "kinesthetic-q5",
        prompt: "I remember information better when:",
        statements: [
          { id: "kinesthetic-q5-s1", text: "I see it visually", style: "Visual" },
          { id: "kinesthetic-q5-s2", text: "I hear it repeatedly", style: "Auditory" },
          { id: "kinesthetic-q5-s3", text: "I write it down", style: "Reading/Writing" },
          { id: "kinesthetic-q5-s4", text: "I act it out or use it in real life", style: "Kinesthetic" },
        ],
      },
    ],
  },
  {
    name: "Multimodal",
    description: "Learn best through a combination of different learning styles",
    icon: Brain,
    color: "from-indigo-500 to-purple-600",
    bgColor: "bg-indigo-50",
    textColor: "text-indigo-700",
    borderColor: "border-indigo-200",
    questions: [
      {
        id: "multimodal-q1",
        prompt: "I usually:",
        statements: [
          { id: "multimodal-q1-s1", text: "Use both pictures and words to understand", style: "Visual" },
          { id: "multimodal-q1-s2", text: "Learn by listening and asking questions", style: "Auditory" },
          { id: "multimodal-q1-s3", text: "Write and highlight notes", style: "Reading/Writing" },
          { id: "multimodal-q1-s4", text: "Combine different methods", style: "Multimodal" },
        ],
      },
      {
        id: "multimodal-q2",
        prompt: "When preparing for an exam, I:",
        statements: [
          { id: "multimodal-q2-s1", text: "Watch videos and review diagrams", style: "Visual" },
          { id: "multimodal-q2-s2", text: "Listen to recordings and discuss with others", style: "Auditory" },
          { id: "multimodal-q2-s3", text: "Read and rewrite notes", style: "Reading/Writing" },
          { id: "multimodal-q2-s4", text: "Use all of the above methods", style: "Multimodal" },
        ],
      },
      {
        id: "multimodal-q3",
        prompt: "I switch learning styles based on:",
        statements: [
          { id: "multimodal-q3-s1", text: "The topic", style: "Visual" },
          { id: "multimodal-q3-s2", text: "The teacher's style", style: "Auditory" },
          { id: "multimodal-q3-s3", text: "Available resources", style: "Reading/Writing" },
          { id: "multimodal-q3-s4", text: "All of the above", style: "Multimodal" },
        ],
      },
      {
        id: "multimodal-q4",
        prompt: "I prefer group projects where I can:",
        statements: [
          { id: "multimodal-q4-s1", text: "Create visuals and slides", style: "Visual" },
          { id: "multimodal-q4-s2", text: "Lead discussions", style: "Auditory" },
          { id: "multimodal-q4-s3", text: "Write the report", style: "Reading/Writing" },
          { id: "multimodal-q4-s4", text: "Do hands-on parts", style: "Multimodal" },
        ],
      },
      {
        id: "multimodal-q5",
        prompt: "My learning style is:",
        statements: [
          { id: "multimodal-q5-s1", text: "Mostly visual", style: "Visual" },
          { id: "multimodal-q5-s2", text: "Mostly auditory", style: "Auditory" },
          { id: "multimodal-q5-s3", text: "Mostly reading/writing", style: "Reading/Writing" },
          { id: "multimodal-q5-s4", text: "A mix of all styles", style: "Multimodal" },
        ],
      },
    ],
  },
]

export default function LearningStyleAssessment() {
  const location = useLocation();
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState({})
  const [showResults, setShowResults] = useState(false)
  const [showReport, setShowReport] = useState(false)
  const [resultsSaved, setResultsSaved] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [aiReportLoading, setAiReportLoading] = useState(false)
  const [aiReportError, setAiReportError] = useState(null)
  const [aiReportText, setAiReportText] = useState("")
  const [autoNext, setAutoNext] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const currentCategory = learningStyles[currentSectionIndex]
  const currentQuestion = currentCategory.questions[currentQuestionIndex]
  const totalSections = learningStyles.length

  const handleAnswerChange = (questionId, statementId, value) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: {
        [statementId]: value,
      },
    }));
    setAutoNext(true);
  }
  useEffect(() => {
    if (autoNext) {
      handleNext();
      setAutoNext(false);
    }
    // eslint-disable-next-line
  }, [autoNext]);

  const totalQuestions = useMemo(() => {
    return learningStyles.reduce((acc, category) => acc + category.questions.length, 0)
  }, [])

  const answeredQuestionsCount = useMemo(() => {
    let count = 0
    learningStyles.forEach((category) => {
      category.questions.forEach((question) => {
        if (answers[question.id] && Object.values(answers[question.id]).some(value => value === "selected")) {
          count++
        }
      })
    })
    return count
  }, [answers])

  const currentSectionAnsweredCount = useMemo(() => {
    let count = 0
    currentCategory.questions.forEach((question) => {
      if (answers[question.id] && Object.values(answers[question.id]).some(value => value === "selected")) {
        count++
      }
    })
    return count
  }, [answers, currentCategory])

  const sectionScores = useMemo(() => {
    return learningStyles.map((category) => {
      let count = 0
      category.questions.forEach((question) => {
        if (answers[question.id] && Object.values(answers[question.id]).some(value => value === "selected")) {
          count++
        }
      })
      return count
    })
  }, [answers])

  const canGoNext = currentSectionIndex < totalSections - 1 || currentQuestionIndex < currentCategory.questions.length - 1
  const canGoPrevious = currentSectionIndex > 0 || currentQuestionIndex > 0
  const isLastQuestion = currentSectionIndex === totalSections - 1 && currentQuestionIndex === currentCategory.questions.length - 1

  // Compute how many statements across the whole quiz map to each learning style
  const maxScoreByStyle = useMemo(() => {
    const counts = {
      "Visual": 0,
      "Auditory": 0,
      "Reading/Writing": 0,
      "Kinesthetic": 0,
      "Multimodal": 0,
    }
    learningStyles.forEach(category => {
      category.questions.forEach(q => {
        q.statements.forEach(s => {
          counts[s.style] = (counts[s.style] || 0) + 1
        })
      })
    })
    return counts
  }, [])

  // Save results when they are first shown
  useEffect(() => {
    if (showResults) {
      saveTestResult();
      try {
        const results = calculateResults;
        if (results && results.length > 0) {
          const top = results[0];
          const totalScore = results.reduce((sum, r) => sum + r.score, 0);
          const totalMaxScore = results.reduce((sum, r) => sum + (maxScoreByStyle[r.style] || 0), 0);
          const overallPercentage = totalMaxScore > 0 ? Math.round((totalScore / totalMaxScore) * 100) : 0;
          const testData = {
            scores: { personalityType: top.style },
            overallScore: overallPercentage,
            overallPercentage,
            testType: 'learningstyle',
            completed: true
          };
          notifyTestCompletion('learningstyle', testData);
        }
      } catch (e) {
        // no-op
      }
    }
  }, [showResults]);

  // Check for showResults URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const showResultsParam = params.get('showResults');
    if (showResultsParam === 'true') {
      setShowResults(true);
    }
  }, [location.search]);

  const overallProgress = (answeredQuestionsCount / totalQuestions) * 100
  const isCurrentQuestionComplete = useMemo(() => {
    if (!answers[currentQuestion.id]) return false;
    return Object.values(answers[currentQuestion.id]).some(value => value === "selected");
  }, [answers, currentQuestion.id])

  const handleNext = () => {
    if (currentQuestionIndex < currentCategory.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1)
    } else if (currentSectionIndex < totalSections - 1) {
      setCurrentSectionIndex(prev => prev + 1)
      setCurrentQuestionIndex(0)
    } else {
      setShowResults(true)
    }
  }

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1)
    } else if (currentSectionIndex > 0) {
      setCurrentSectionIndex(prev => prev - 1)
      setCurrentQuestionIndex(learningStyles[currentSectionIndex - 1].questions.length - 1)
    }
  }

  const calculateResults = useMemo(() => {
    const scores = {
      "Visual": 0,
      "Auditory": 0,
      "Reading/Writing": 0,
      "Kinesthetic": 0,
      "Multimodal": 0,
    }

    learningStyles.forEach((category) => {
      category.questions.forEach((question) => {
        question.statements.forEach((statement) => {
          const selectedOption = answers[question.id]?.[statement.id]
          if (selectedOption === "selected") {
            scores[statement.style] += 1
          }
        })
      })
    })

    const sortedStyles = Object.entries(scores)
      .map(([style, score]) => ({
        style,
        score,
        category: learningStyles.find(cat => cat.name === style)
      }))
      .sort((a, b) => b.score - a.score)

    return sortedStyles
  }, [answers])

  const saveTestResult = async () => {
    try {
      const results = calculateResults;
      const detailedAnswers = [];

      learningStyles.forEach((category) => {
        category.questions.forEach((question) => {
          question.statements.forEach((statement) => {
            const selectedOption = answers[question.id]?.[statement.id];
            if (selectedOption === "selected") {
              detailedAnswers.push({
                questionId: question.id,
                questionText: question.prompt,
                statementId: statement.id,
                statementText: statement.text,
                selectedOptionId: statement.id,
                selectedOptionText: statement.text,
                learningStyle: statement.style
              });
            }
          });
        });
      });

      // Calculate overall statistics
      const totalScore = results.reduce((sum, result) => sum + result.score, 0);
      const totalMaxScore = results.reduce((sum, result) => sum + maxScoreByStyle[result.style], 0);
      const overallPercentage = Math.round((totalScore / totalMaxScore) * 100);
      // Save using generic results endpoint
      await api.post('/api/test-results', {
        testType: 'learningstyle',
        answers: detailedAnswers,
        scores: results.reduce((acc, result) => {
          acc[result.style] = result.score;
          return acc;
        }, {}),
        overallScore: totalScore,
        overallPercentage
      });

      toast.success('Learning Style Test results saved successfully!');
      setResultsSaved(true);

    } catch (error) {
      console.error('Error saving Learning Style Test result:', error.response?.data || error.message);
      toast.error(error.response?.data?.message || 'Failed to save test results');
    }
  };

  const fetchAIReport = async () => {
    try {
      setAiReportLoading(true);
      setAiReportError(null);
      const detailedAnswers = [];
      learningStyles.forEach((category) => {
        category.questions.forEach((question) => {
          question.statements.forEach((statement) => {
            const selectedOption = answers[question.id]?.[statement.id];
            if (selectedOption === "selected") {
              detailedAnswers.push({
                questionId: question.id,
                questionText: question.prompt,
                statementId: statement.id,
                statementText: statement.text,
                selectedOptionId: statement.id,
                selectedOptionText: statement.text,
                learningStyle: statement.style
              });
            }
          });
        });
      });

      const resultsArr = calculateResults;
      const scoresObj = resultsArr.reduce((acc, result) => {
        acc[result.style] = result.score;
        return acc;
      }, {});
      const totalScore = resultsArr.reduce((sum, r) => sum + r.score, 0);
      const totalMaxScore = resultsArr.reduce((sum, r) => sum + (maxScoreByStyle[r.style] || 0), 0);
      const overallPercentage = totalMaxScore > 0 ? Math.round((totalScore / totalMaxScore) * 100) : 0;

      const payload = {
        testType: 'learningstyle',
        scores: scoresObj,
        answers: detailedAnswers,
        overallScore: totalScore,
        overallPercentage
      };

      const { report } = await generateAIReport(payload);
      setAiReportText(report);
    } catch (err) {
      setAiReportError(err?.response?.data?.message || 'Failed to generate AI report');
    } finally {
      setAiReportLoading(false);
    }
  };

  useEffect(() => {
    if (showReport && !aiReportText && !aiReportLoading) {
      fetchAIReport();
    }
  }, [showReport]);


  const getLevel = (percentage) => {
    if (percentage >= 80) return { level: 'Very High', color: 'bg-red-500' }
    if (percentage >= 65) return { level: 'High', color: 'bg-orange-500' }
    if (percentage >= 50) return { level: 'Moderate', color: 'bg-yellow-500' }
    if (percentage >= 35) return { level: 'Low', color: 'bg-blue-500' }
    return { level: 'Very Low', color: 'bg-green-500' }
  }

  const getLearningStyleDescription = (styleName, percentage) => {
    const style = learningStyles.find(s => s.name === styleName);
    if (!style) return "";

    let description = style.description;
    if (percentage >= 70) {
      description += " You excel in this area and are likely to be a strong performer in roles requiring this style.";
    } else if (percentage >= 40) {
      description += " You have a good understanding of this style but could benefit from specific strategies.";
    } else {
      description += " You might need to work on developing this style to improve your learning effectiveness.";
    }
    return description;
  };

  const getStudyStrategies = (styleName) => {
    const style = learningStyles.find(s => s.name === styleName);
    if (!style) return [];

    switch (styleName) {
      case "Visual":
        return [
          "Use diagrams, charts, and graphs to visualize concepts.",
          "Create mind maps or concept maps to organize information.",
          "Take detailed notes during lectures, including visual elements.",
          "Practice by drawing or sketching concepts.",
          "Use color-coding and highlighting in textbooks."
        ];
      case "Auditory":
        return [
          "Listen actively to lectures and discussions.",
          "Repeat information out loud to reinforce memory.",
          "Use audio recordings for studying.",
          "Engage in group discussions and debates.",
          "Practice public speaking."
        ];
      case "Reading/Writing":
        return [
          "Read and re-read text-based materials.",
          "Take detailed notes in your own words.",
          "Write summaries and practice writing exercises.",
          "Use mnemonic devices for memorization.",
          "Break down complex concepts into smaller, manageable parts."
        ];
      case "Kinesthetic":
        return [
          "Engage in hands-on activities and practical exercises.",
          "Role-play scenarios or act out concepts.",
          "Use physical models or props to understand concepts.",
          "Practice physical movements or gestures.",
          "Incorporate movement breaks into study sessions."
        ];
      case "Multimodal":
        return [
          "Combine visual, auditory, and reading/writing strategies.",
          "Use a variety of resources (books, videos, online courses).",
          "Take detailed notes that incorporate both visual and written elements.",
          "Engage in discussions and debates.",
          "Practice by creating visual representations of concepts."
        ];
      default:
        return [];
    }
  };

  const getLearningTools = (styleName) => {
    const style = learningStyles.find(s => s.name === styleName);
    if (!style) return [];

    switch (styleName) {
      case "Visual":
        return [
          "Whiteboards and markers for brainstorming.",
          "Post-it notes for quick reminders.",
          "Mind mapping software (e.g., MindMeister, Miro).",
          "Visualization tools (e.g., Lucidchart, Draw.io).",
          "Highlighters and colored pens."
        ];
      case "Auditory":
        return [
          "Good quality headphones or speakers.",
          "Audio books and podcasts.",
          "Voice recording apps for note-taking.",
          "Online lectures and webinars.",
          "Music that helps focus."
        ];
      case "Reading/Writing":
        return [
          "Digital note-taking apps (e.g., OneNote, Evernote).",
          "Text-to-speech software for reading.",
          "E-books and online textbooks.",
          "Writing software (e.g., Microsoft Word, Google Docs).",
          "Highlighters and sticky notes."
        ];
      case "Kinesthetic":
        return [
          "Physical exercise equipment (e.g., yoga mat, weights).",
          "Sports equipment for physical activities.",
          "Physical models or props for demonstrations.",
          "Movement-based games and apps.",
          "Physical therapy tools."
        ];
      case "Multimodal":
        return [
          "Multiple monitors for visual work.",
          "High-quality speakers for audio.",
          "High-resolution tablets for note-taking.",
          "Virtual reality tools for immersive learning.",
          "Interactive whiteboards."
        ];
      default:
        return [];
    }
  };

  const getCareerPaths = (styleName) => {
    const style = learningStyles.find(s => s.name === styleName);
    if (!style) return [];

    switch (styleName) {
      case "Visual":
        return ["Graphic Designer", "Architect", "Interior Designer", "Fashion Designer", "Photographer"];
      case "Auditory":
        return ["Teacher", "Public Speaker", "Salesperson", "Event Planner", "Musician"];
      case "Reading/Writing":
        return ["Writer", "Editor", "Journalist", "Researcher", "Technical Writer"];
      case "Kinesthetic":
        return ["Physical Therapist", "Athlete", "Dance Instructor", "Personal Trainer", "Sports Coach"];
      case "Multimodal":
        return ["UX/UI Designer", "Digital Marketer", "Content Creator", "Video Producer", "Multimedia Specialist"];
      default:
        return [];
    }
  };

  const getPersonalDevelopment = (styleName) => {
    const style = learningStyles.find(s => s.name === styleName);
    if (!style) return "";

    switch (styleName) {
      case "Visual":
        return "Embrace your visual strengths by incorporating more visual elements into your daily life. Use color-coding, diagrams, and mind maps to enhance your learning. Consider taking art classes or practicing drawing to improve your visual skills.";
      case "Auditory":
        return "Leverage your auditory strengths by actively engaging with spoken language. Practice public speaking, join a book club, and listen to educational podcasts. This will not only improve your learning but also your communication skills.";
      case "Reading/Writing":
        return "Develop your reading/writing skills by setting a consistent reading habit and practicing writing regularly. Use digital tools to enhance your note-taking and reading experience. Consider joining a writing group or taking a creative writing course.";
      case "Kinesthetic":
        return "Utilize your kinesthetic strengths by incorporating physical movement into your daily routine. Engage in sports, dance, or yoga. This will not only improve your physical health but also enhance your learning through active engagement.";
      case "Multimodal":
        return "Embrace your multimodal strengths by combining different learning methods. Use a variety of resources and tools to tailor your learning to your preferences. Consider taking courses that incorporate both visual and auditory elements.";
      default:
        return "";
    }
  };

  const getLearningEnvironment = (styleName) => {
    const style = learningStyles.find(s => s.name === styleName);
    if (!style) return "";

    switch (styleName) {
      case "Visual":
        return "Create a visually stimulating environment. Use a large monitor, invest in a good ergonomic chair, and ensure your workspace is well-lit. Consider using a whiteboard or a digital canvas for brainstorming.";
      case "Auditory":
        return "Ensure you have access to high-quality audio equipment. Use noise-cancelling headphones or a good speaker system. Consider a quiet study space away from distractions.";
      case "Reading/Writing":
        return "Set up a comfortable and ergonomic workspace. Use a proper chair, good lighting, and a distraction-free environment. Consider using a standing desk or a desk with adjustable height.";
      case "Kinesthetic":
        return "Create a space conducive to physical movement. Incorporate a yoga ball or a stability ball into your chair, use a treadmill desk, or set up a mini workout area. Ensure your workspace is spacious enough for physical activities.";
      case "Multimodal":
        return "Combine different environments to suit your learning needs. Use a combination of visual aids, audio, and physical activities. Consider having multiple workspaces or using a flexible workspace.";
      default:
        return "";
    }
  };

  if (showIntro) {
    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-[#EFF6FF] via-white to-[#F9FAFB] relative">
        {/* Sidebar */}
        <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
          <Sidebar />
        </div>

        {/* Main content */}
        <div className="ml-64">
          <Navbar />

          <div className="w-full max-w-7xl mx-auto pt-10 pb-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mx-auto">
              <Card className="text-center bg-white shadow-lg">
                <CardHeader className="space-y-4">
                  <CardTitle className="text-3xl font-bold">Learning Style Assessment</CardTitle>
                  <CardDescription className="text-lg text-gray-600">
                    Discover how you learn best & unlock strategies tailored to your style
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                    <div className="space-y-2">
                      <h3 className="font-semibold text-purple-600">Visual</h3>
                      <p className="text-sm text-gray-600">Learning with diagrams, charts & visual aids</p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-blue-600">Auditory</h3>
                      <p className="text-sm text-gray-600">Learning through listening & discussions</p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-green-600">Reading/Writing</h3>
                      <p className="text-sm text-gray-600">Learning with text, notes & reading</p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-red-600">Kinesthetic</h3>
                      <p className="text-sm text-gray-600">Learning by doing & hands-on practice</p>
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <h3 className="font-semibold text-orange-600">Multimodal</h3>
                      <p className="text-sm text-gray-600">A mix of different learning styles</p>
                    </div>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-purple-800">
                      <strong>{learningStyles.reduce((acc, cat) => acc + cat.questions.length, 0)} questions</strong>• Gain personalized learning strategies
                    </p>
                  </div>

                  <Button
                    onClick={() => setShowIntro(false)}
                    className="w-full text-lg py-6 bg-purple-600 hover:bg-purple-700"
                    size="lg"
                  >
                    Start Test <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (showResults) {
    if (showReport) {
      // Detailed report view
      return (
        <div className="flex flex-col min-h-screen w-full overflow-x-hidden" style={{ background: 'linear-gradient(90deg, #FFFFFF 0%, #EFF6FF 100%, #F9FAFB 100%)' }}>
          <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
            <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
          </div>
          <div className="ml-0 md:ml-64 min-h-screen">
            <Navbar toggleSidebar={toggleSidebar} />
            <div className="min-h-screen p-4">
              <div className="mx-auto space-y-6" style={{ width: '85%' }}>
                <Card className='bg-[#ffffff]'>
                  <CardHeader>
                    <CardTitle className="text-2xl font-bold flex items-center">
                      <FileText className="mr-3 w-6 h-6" />
                      Detailed Learning Style Report
                    </CardTitle>
                    <CardDescription>Comprehensive analysis of your learning preferences</CardDescription>
                  </CardHeader>
                </Card>

                <Card className='bg-[#ffffff]'>
                  <CardHeader>
                    <CardTitle className="text-xl font-semibold">We are collecting your results</CardTitle>
                    <CardDescription>Personalized insights</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {aiReportLoading && (
                      <div className="flex items-center text-gray-600">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                        Generating your report...
                      </div>
                    )}
                    {aiReportError && (
                      <div className="text-red-600 text-sm mb-2">{aiReportError}</div>
                    )}
                    {!aiReportLoading && aiReportText && (
                      <div className="prose max-w-none whitespace-pre-wrap text-gray-800 text-sm">{aiReportText}</div>
                    )}
                    {!aiReportLoading && !aiReportText && (
                      <Button onClick={fetchAIReport} className="bg-blue-600 hover:bg-blue-700 text-white">Generate Report</Button>
                    )}
                  </CardContent>
                </Card>

                {calculateResults.map((result) => {
                  const styleName = result.style
                  const maxPos = maxScoreByStyle[styleName] || 1
                  const percentage = Math.round((result.score / maxPos) * 100)
                  const { level, color } = getLevel(percentage)
                  return (
                    <Card className='bg-[#ffffff]' key={styleName}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-xl">{styleName}</CardTitle>
                            <CardDescription>{result.category?.description}</CardDescription>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold">{result.score}/{maxPos}</div>
                            <span className={`px-3 py-1 rounded-full text-sm text-white ${color}`}>{level}</span>
                          </div>
                        </div>
                        <Progress value={percentage} className="h-2" />
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Enhanced Content */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-semibold text-gray-800 mb-2">Your Learning Profile</h4>
                          <p className="text-gray-700">{getLearningStyleDescription(styleName, percentage)}</p>
                        </div>

                        {/* Study Strategies */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-green-50 rounded-lg p-4">
                            <h4 className="font-semibold text-green-800 mb-2">Recommended Study Strategies</h4>
                            <ul className="space-y-1">
                              {getStudyStrategies(styleName).map((strategy, index) => (
                                <li key={index} className="flex items-start gap-2">
                                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                  <span className="text-green-700 text-sm">{strategy}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="bg-orange-50 rounded-lg p-4">
                            <h4 className="font-semibold text-orange-800 mb-2">Learning Tools</h4>
                            <ul className="space-y-1">
                              {getLearningTools(styleName).map((tool, index) => (
                                <li key={index} className="flex items-start gap-2">
                                  <Star className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                                  <span className="text-orange-700 text-sm">{tool}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        {/* Career & Personal Development */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div className="bg-blue-50 rounded-lg p-4">
                            <h4 className="font-semibold text-blue-800 mb-2">Career Paths</h4>
                            <div className="flex flex-wrap gap-2">
                              {getCareerPaths(styleName).map((career, index) => (
                                <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                  {career}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="bg-purple-50 rounded-lg p-4">
                            <h4 className="font-semibold text-purple-800 mb-2">Personal Development</h4>
                            <p className="text-purple-700 text-sm leading-relaxed">{getPersonalDevelopment(styleName)}</p>
                          </div>
                        </div>

                        {/* Learning Environment */}
                        <div className="bg-emerald-50 rounded-lg p-4">
                          <h4 className="font-semibold text-emerald-800 mb-2">Optimal Learning Environment</h4>
                          <p className="text-emerald-700 text-sm leading-relaxed">{getLearningEnvironment(styleName)}</p>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}

                <div className="flex gap-4">
                  <Button onClick={() => setShowReport(false)} variant="outline" className="flex-1 bg-[#ffffff]">
                    Back to Results
                  </Button>
                  <Button
                    onClick={() => {
                      setShowResults(false)
                      setShowReport(false)
                      setCurrentSectionIndex(0)
                      setCurrentQuestionIndex(0)
                      setAnswers({})
                      setResultsSaved(false)
                    }}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    Retake Test
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
    return (
      <div className="flex flex-col min-h-screen w-full overflow-x-hidden" style={{ background: 'linear-gradient(90deg, #FFFFFF 0%, #EFF6FF 100%, #F9FAFB 100%)' }}>
        <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
          <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
        </div>
        <div className="ml-0 md:ml-64 min-h-screen">
          <Navbar toggleSidebar={toggleSidebar} />

          <div className="min-h-screen p-4">
            <div className="mx-auto" style={{ width: '85%' }}>
              <Card className='bg-[#ffffff]'>
                <CardHeader className="text-center">
                  <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <Brain className="w-8 h-8 text-purple-600" />
                  </div>
                  <CardTitle className="text-3xl font-bold">Your Learning Style Results</CardTitle>
                  <CardDescription>Based on the VARK Learning Styles Model</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {calculateResults.map((result) => {
                    const styleName = result.style
                    const maxPos = maxScoreByStyle[styleName] || 1
                    const percentage = Math.round((result.score / maxPos) * 100)
                    const { level, color } = getLevel(percentage)
                    return (
                      <div key={styleName} className="space-y-3">
                        <div className="flex justify-between items-center">
                          <h3 className="font-semibold text-lg">{styleName}</h3>
                          <div className="text-right">
                            <span className="font-bold">{result.score}/{maxPos}</span>
                            <span className={`ml-2 px-2 py-1 rounded text-xs text-white ${color}`}>{level}</span>
                          </div>
                        </div>
                        <Progress value={percentage} className="h-3" />
                        <p className="text-sm text-gray-600">{result.category?.description}</p>
                      </div>
                    )
                  })}
                  <div className="flex gap-4 pt-6">
                    <Button onClick={() => setShowReport(true)} className="flex-1 bg-purple-600 hover:bg-purple-700 text-white">
                      <FileText className="mr-2 w-4 h-4" />
                      View Detailed Report
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowResults(false)
                        setShowReport(false)
                        setCurrentSectionIndex(0)
                        setCurrentQuestionIndex(0)
                        setAnswers({})
                        setResultsSaved(false)
                      }}
                    >
                      <RotateCcw className="mr-2 w-4 h-4" />
                      Retake Test
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden" style={{ background: 'linear-gradient(90deg, #FFFFFF 0%, #EFF6FF 100%, #F9FAFB 100%)' }}>
      <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
        <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      </div>
      <div className="ml-0 md:ml-64 min-h-screen " >
        <Navbar
          toggleSidebar={toggleSidebar}
        />
        <div className=" mx-auto" style={{ width: '85%' }}>
          {/* Header */}
          <div className="text-center mb-8 mt-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Learning Style Test</h1>
            <p className="text-xl text-gray-600">Discover how you learn best and optimize your study strategies</p>
          </div>

          {/* Progress by Section */}
          <Card className="rounded-lg border text-card-foreground shadow-sm mb-6 bg-[#ffffff] ">
            <CardHeader>
              <CardTitle className="text-lg">Your Progress by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-4 mb-6">
                {learningStyles.map((section, index) => {
                  const Icon = section.icon
                  return (
                    <div key={section.name} className="text-center">
                      <div className={`w-12 h-12 mx-auto mb-2 rounded-full ${section.bgColor} flex items-center justify-center`}>
                        <Icon className={`w-6 h-6 ${section.textColor}`} />
                      </div>
                      <h3 className="font-medium text-sm mb-1">{section.name}</h3>
                      <p className="text-xs text-gray-500">{sectionScores[index]}/5</p>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-600">
                Question {currentSectionIndex * 5 + currentQuestionIndex + 1} of {totalQuestions}
              </span>
              <span className="text-sm text-gray-600">
                {Math.round(overallProgress)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          </div>

          {/* Question Card */}
          <Card className="rounded-lg border text-card-foreground shadow-sm bg-[#ffffff]">
            <CardHeader className="pb-0 border-b">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <CardTitle className="font-semibold tracking-tight text-xl mb-2">
                    {currentSectionIndex + 1}.{currentQuestionIndex + 1} {currentCategory.name}
                  </CardTitle>
                  <p className="text-sm text-gray-500 mb-4">{currentCategory.description}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <h3 className="text-lg font-medium mb-6 text-gray-900 mt-3">
                {currentQuestion.prompt}
              </h3>

              <div className="space-y-3">
                {currentQuestion.statements.map((statement, index) => {
                  const isSelected = answers[currentQuestion.id]?.[statement.id] === "selected"
                  return (
                    <div
                      key={statement.id}
                      onClick={() => handleAnswerChange(currentQuestion.id, statement.id, "selected")}
                      className={`flex items-center rounded-xl border-2 p-2 px-4 cursor-pointer transition-all duration-200 ${isSelected
                        ? "border-primary ring-2 ring-primary/50 bg-primary/5 dark:bg-primary/10"
                        : "hover:bg-[rgb(197,199,203)] dark:hover:bg-[rgb(197,199,203)]0"
                        }`}
                    >
                      <div className="flex items-center gap-4 w-full">
                        <div className="flex items-center gap-4 w-full">
                          <div className="flex-1">
                            <div>{statement.text}</div>
                          </div>
                        </div>

                      </div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex justify-between items-center m-5">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={!canGoPrevious}
              className="bg-[#ffffff] px-4 py-2 rounded-md text-sm font-medium transition-colors focus:outline-none border border-gray-300 text-gray-700 bg-white hover:bg-gray-100"
            >
              Previous
            </Button>

            {/* <Button
              onClick={handleNext}
              disabled={!isCurrentQuestionComplete}
              className={`bg-purple-600 hover:bg-purple-700 text-white flex items-center space-x-2`}
            >
              {isLastQuestion ? "View Results" : canGoNext ? "Next Question" : "Next Section"}
            </Button> */}
          </div>
        </div>
      </div>
    </div>
  )
}
